import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'react-uuid';
import moment from 'moment';
import 'moment/locale/en-gb';
import { useConfig } from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';
import { numberToString } from '../../special/numberConverter';
import TypeNumber from './TypeNumber';
import { round } from '../../special/round';

export const meansOfTransportationOptions = [
  { label: 'Company Car', value: 'company-car' },
  { label: 'Private Car', value: 'private-car' },
  { label: 'Train', value: 'train' },
  { label: 'Bus', value: 'bus' },
  { label: 'Taxi', value: 'taxi' },
  { label: 'Bicycle', value: 'bicycle' },
  { label: 'Airplane', value: 'airplane' },
  { label: 'Boat', value: 'boat' },
  { label: 'On Foot', value: 'on-foot' },
];

const DATETIME_VALUE_FORMAT = 'YYYY-MM-DDTHH:mm';

export function toDateTimeInput(value) {
  return value ? moment(value).format(DATETIME_VALUE_FORMAT) : '';
}

/** Number() for values as TypeNumber stores them (dot-normalized strings),
 *  tolerating a German decimal comma from older drafts. */
export function parseDecimal(value) {
  return Number(String(value ?? '').replace(',', '.'));
}

/** The mileage for one leg. Distance times rate is not a rule that can drift,
 *  so it is shown immediately rather than waited on from the server. */
export function legAmount(leg) {
  return round(
    (parseDecimal(leg.distanceKilometers) || 0) *
      (parseDecimal(leg.pricePerKilometer) || 0),
    2,
  );
}

export function emptyLeg(defaults = {}) {
  return {
    uuid: uuid(),
    fromLocation: '',
    toLocation: '',
    departedAt: '',
    arrivedAt: '',
    meansOfTransportation: 'private-car',
    distanceKilometers: '',
    pricePerKilometer: defaults.pricePerKilometer ?? '',
    isReturnLeg: false,
  };
}

/** A leg prefilled from a saved route. Times stay empty for the employee. */
export function legFromPreset(preset, defaults = {}) {
  return {
    ...emptyLeg(defaults),
    travelExpensePresetId: preset.id,
    fromLocation: preset.fromLocation || '',
    toLocation: preset.toLocation || preset.destinationAddress?.city || '',
    distanceKilometers: preset.distanceKilometers ?? '',
    pricePerKilometer:
      preset.pricePerKilometer ?? defaults.pricePerKilometer ?? '',
    toAddress: preset.destinationAddress || undefined,
  };
}

/** The picker name is generated: the route, or failing that the destination. */
export function presetLabel(preset) {
  return (
    [preset.fromLocation, preset.toLocation].filter(Boolean).join(' → ') ||
    preset.destinationAddress?.city ||
    `${preset.distanceKilometers} km`
  );
}

// One shared template so the header and every row stay aligned. The fixed
// widths carry the pickers and numbers; From/To share the flexible rest. The
// sum of the minimums must stay below the card's width inside the standard
// 1250px dialog, or the table scrolls when it should not.
const GRID_TEMPLATE_COLUMNS =
  '18px minmax(68px, 1.2fr) minmax(68px, 1.2fr) 160px 160px 110px 56px 52px 52px 28px';

const rowSx = {
  display: 'grid',
  gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
  gap: '6px',
  alignItems: 'center',
};

const HeaderCell = ({ children, align }) => (
  <Typography
    component="span"
    className="uppercase truncate"
    sx={{
      fontSize: '10.5px',
      fontWeight: 600,
      letterSpacing: '0.05em',
      opacity: 0.6,
      textAlign: align || 'left',
    }}
  >
    {children}
  </Typography>
);

/**
 * The travel log (Reiseverlauf): an ordered list of movements, each from one
 * place at one time to another place at another time.
 *
 * Dense table layout: one grid row per leg under a shared column header, so
 * labels are not repeated per field and the eye can follow one trip line by
 * line (Tab flows left to right). The table scrolls horizontally inside its
 * card when the viewport is narrower than the columns.
 *
 * Departure and arrival use the application's standard MUI X pickers (moment
 * adapter, 24h clock, configured date format), matching every other date field.
 */
const TypeTravelExpenseLegs = ({
  field,
  dataCollection,
  errors = {},
  updateValidationOnChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const dateFormat = config?.general?.dateOutputFormat || 'DD.MM.YYYY';

  // "en-gb" uses Monday as the first day of the week (same as TypeDate).
  useEffect(() => {
    moment.locale('en-gb');
  }, []);

  const legs = dataCollection[field.key] || [];
  const currencySymbol = field.currencySymbol ?? '';
  const error = errors[field.key];

  function commit(newLegs) {
    updateValidationOnChange(field.key, newLegs);
  }

  function handleChange(index, key, value) {
    const newLegs = [...legs];
    newLegs[index] = { ...newLegs[index], [key]: value };
    commit(newLegs);
  }

  /**
   * The km rate only exists for the private car — train, bus or taxi rides are
   * billed via their receipts (additional costs) or cost nothing. Switching
   * the transportation therefore switches the rate: 0 away from the private
   * car, and back to it the rate the leg had before (a preset or a saved leg
   * may carry its own), falling back to the configured default.
   */
  function handleTransportChange(index, value) {
    const leg = legs[index];
    const wasPrivateCar =
      (leg.meansOfTransportation || 'private-car') === 'private-car';
    const newLegs = [...legs];

    if (value === 'private-car') {
      const restored =
        leg.previousPricePerKilometer ??
        (wasPrivateCar ? leg.pricePerKilometer : undefined);
      newLegs[index] = {
        ...leg,
        meansOfTransportation: value,
        pricePerKilometer:
          restored === undefined || restored === ''
            ? (field.defaultPricePerKilometer ?? '')
            : restored,
        previousPricePerKilometer: undefined,
      };
    } else {
      newLegs[index] = {
        ...leg,
        meansOfTransportation: value,
        pricePerKilometer: 0,
        previousPricePerKilometer: wasPrivateCar
          ? leg.pricePerKilometer
          : leg.previousPricePerKilometer,
      };
    }
    commit(newLegs);
  }

  function handleDateTimeChange(index, key, newValue) {
    const value =
      newValue && newValue.isValid() ? newValue.format(DATETIME_VALUE_FORMAT) : '';
    const leg = legs[index];

    // The arrival follows the departure (day and time) until it is set to
    // something of its own: picking a departure should not force re-picking
    // the same day in the arrival field. "Of its own" is detected by value —
    // while the arrival still mirrors the departure it keeps following.
    if (
      key === 'departedAt' &&
      value &&
      (!leg.arrivedAt || leg.arrivedAt === leg.departedAt)
    ) {
      const newLegs = [...legs];
      newLegs[index] = { ...leg, departedAt: value, arrivedAt: value };
      return commit(newLegs);
    }

    handleChange(index, key, value);
  }

  function handleAdd() {
    const last = legs[legs.length - 1];
    commit([
      ...legs,
      {
        ...emptyLeg({ pricePerKilometer: field.defaultPricePerKilometer }),
        // "From" continues where the last leg ended (see the footer hint).
        fromLocation: last?.toLocation || '',
      },
    ]);
  }

  function handleDelete(index) {
    commit(legs.filter((_, i) => i !== index));
  }

  /**
   * "There and back" as one click: mirror the last leg, swapping its endpoints.
   * The employee can then correct times and details on the new row.
   */
  function handleAddReturnLeg() {
    const last = legs[legs.length - 1];
    if (!last) return handleAdd();

    commit([
      ...legs,
      {
        ...last,
        uuid: uuid(),
        id: undefined,
        fromLocation: last.toLocation,
        toLocation: last.fromLocation,
        // The preset link and its destination snapshot describe the outbound
        // destination; the way back has neither.
        travelExpensePresetId: undefined,
        toAddress: undefined,
        departedAt: '',
        arrivedAt: '',
        isReturnLeg: true,
      },
    ]);
  }

  // Saved routes for the selected project. undefined = no project picked yet,
  // [] = the project has none; both keep the button visible but disabled so the
  // feature stays discoverable.
  const presets = field.presets;
  const [presetAnchor, setPresetAnchor] = useState(null);

  function handleAddFromPreset(preset) {
    setPresetAnchor(null);
    commit([
      ...legs.filter((leg) => leg.departedAt || leg.fromLocation || leg.toLocation || leg.distanceKilometers),
      legFromPreset(preset, { pricePerKilometer: field.defaultPricePerKilometer }),
    ]);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box
        sx={{
          gridColumn: field.colspan
            ? `span ${field.colspan} / span ${field.colspan}`
            : 'auto',
        }}
        className="flex flex-col w-full"
      >
        <Box className="overflow-x-auto">
          <Box className="flex flex-col px-3 pb-2" sx={{ minWidth: '830px' }}>
            <Box sx={rowSx} className="pt-2 pb-1">
              <span />
              <HeaderCell>{t('From')}</HeaderCell>
              <HeaderCell>{t('To')}</HeaderCell>
              <HeaderCell>{t('Departure')}</HeaderCell>
              <HeaderCell>{t('Arrival')}</HeaderCell>
              <HeaderCell>{t('Means of Transportation')}</HeaderCell>
              <HeaderCell align="right">km</HeaderCell>
              <HeaderCell align="right">{`${currencySymbol}/km`}</HeaderCell>
              <HeaderCell align="right">{t('Leg amount')}</HeaderCell>
              <span />
            </Box>

            {legs.map((leg, index) => (
              <Box key={leg.uuid || leg.id} sx={rowSx} className="py-1">
                {leg.isReturnLeg ? (
                  <Tooltip title={t('Return')}>
                    <SwapVertIcon
                      sx={{ fontSize: '15px' }}
                      className="opacity-60"
                    />
                  </Tooltip>
                ) : (
                  <Typography
                    component="span"
                    variant="caption"
                    className="opacity-60 font-bold"
                  >
                    {index + 1}
                  </Typography>
                )}

                <TextField
                  size="small"
                  value={leg.fromLocation || ''}
                  onChange={(e) => handleChange(index, 'fromLocation', e.target.value)}
                  sx={{ minWidth: 0 }}
                />
                <TextField
                  size="small"
                  value={leg.toLocation || ''}
                  onChange={(e) => handleChange(index, 'toLocation', e.target.value)}
                  sx={{ minWidth: 0 }}
                />

                <DateTimePicker
                  ampm={false}
                  format={`${dateFormat} HH:mm`}
                  value={leg.departedAt ? moment(leg.departedAt) : null}
                  onChange={(newValue) =>
                    handleDateTimeChange(index, 'departedAt', newValue)
                  }
                  slotProps={{ textField: { size: 'small' } }}
                  sx={{ minWidth: 0 }}
                />
                <DateTimePicker
                  ampm={false}
                  format={`${dateFormat} HH:mm`}
                  value={leg.arrivedAt ? moment(leg.arrivedAt) : null}
                  onChange={(newValue) =>
                    handleDateTimeChange(index, 'arrivedAt', newValue)
                  }
                  slotProps={{ textField: { size: 'small' } }}
                  sx={{ minWidth: 0 }}
                />

                <TextField
                  select
                  size="small"
                  value={leg.meansOfTransportation || 'private-car'}
                  onChange={(e) => handleTransportChange(index, e.target.value)}
                  sx={{ minWidth: 0 }}
                >
                  {meansOfTransportationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.label)}
                    </MenuItem>
                  ))}
                </TextField>

                <TypeNumber
                  size="small"
                  field={{ label: '', key: 'distanceKilometers' }}
                  dataCollection={leg}
                  updateValidationOnChange={(key, value) =>
                    handleChange(index, 'distanceKilometers', value)
                  }
                />

                {/* The rate comes from the config or the preset and is not
                    editable here; shown rounded, the exact value is kept. */}
                <Typography
                  component="span"
                  className="monospace whitespace-nowrap opacity-60"
                  sx={{ fontSize: '13px', textAlign: 'right' }}
                >
                  {numberToString(parseDecimal(leg.pricePerKilometer) || 0, 2)}
                </Typography>

                <Typography
                  component="span"
                  className="monospace whitespace-nowrap"
                  sx={{ fontSize: '13px', textAlign: 'right' }}
                >
                  {numberToString(legAmount(leg))}
                </Typography>

                <Tooltip title={t('Delete')}>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(index)}
                    sx={{ ':hover': { color: colors.error } }}
                  >
                    <DeleteIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" className="px-3 pb-2" sx={{ color: colors.error }}>
            {error}
          </Typography>
        )}

        <Box
          className="flex gap-2 items-center flex-wrap px-3 py-2"
          sx={{
            borderTop: `1px solid ${colors.selected}`,
            backgroundColor: colors.bgInput,
          }}
        >
          <Button size="small" startIcon={<AddCircleIcon />} onClick={handleAdd}>
            {t('Add Leg')}
          </Button>
          {legs.length > 0 && (
            <Button
              size="small"
              startIcon={<SwapVertIcon />}
              onClick={handleAddReturnLeg}
            >
              {t('Add Return Leg')}
            </Button>
          )}
          <Tooltip
            title={
              presets === undefined
                ? t('Select a project first')
                : presets.length === 0
                  ? t('No presets for this project')
                  : ''
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<BookmarkAddedIcon />}
                disabled={!presets?.length}
                onClick={(e) => setPresetAnchor(e.currentTarget)}
              >
                {t('From Preset')}
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={presetAnchor}
            open={Boolean(presetAnchor)}
            onClose={() => setPresetAnchor(null)}
          >
            {(presets || []).map((preset) => (
              <MenuItem key={preset.id} onClick={() => handleAddFromPreset(preset)}>
                <Box className="flex flex-col">
                  <Typography variant="body2">{presetLabel(preset)}</Typography>
                  <Typography variant="caption" className="opacity-60 monospace">
                    {`${numberToString(preset.distanceKilometers, 2)} km · ${numberToString(preset.pricePerKilometer, 2)} ${field.currencySymbol || ''}/km`}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>

          <Typography
            variant="caption"
            className="ml-auto opacity-60 hidden lg:block"
          >
            {t('"From" is prefilled from the previous leg')}
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default TypeTravelExpenseLegs;
